import { useEffect, useState } from 'react';
import { api } from '../../api';
import type { GmOnline, GmOnlinePagina, GmSkills, Notoriety } from '../../api/types';
import { idiomaAtual, t } from '../../i18n/idioma';
import { nickFillFromLabel } from '../../look/nickColor';
import type { RegistroMapa } from '../../world/mapa';
import { CoroaVip } from '../CoroaVip';
import { BotaoPerigoso, idDaCidade, rotuloCanal, type JogadorSalvo, type Rodar } from './comum';

/** A busca no servidor começa com 2 letras e traz até 10 (techspec-gm-menu §03). */
export const MIN_BUSCA = 2;
export const LIMITE_BUSCA = 10;

export type Fonte = 'sala' | 'recentes' | 'fixados';

const FLAGS: readonly { id: Notoriety; rotulo: string }[] = [
  { id: 'inocente', rotulo: 'notoriedade.innocentCurto' },
  { id: 'criminoso', rotulo: 'notoriedade.criminalCurto' },
  { id: 'assassino', rotulo: 'notoriedade.murdererCurto' }
];
const NOTORIEDADE_CURTA: Readonly<Record<Notoriety, string>> = {
  inocente: 'notoriedade.innocentCurto',
  criminoso: 'notoriedade.criminalCurto',
  assassino: 'notoriedade.murdererCurto',
  duelista: 'notoriedade.duelistaCurto'
};

interface ListaProps {
  busca: string;
  setBusca(v: string): void;
  achados: GmOnlinePagina | null;
  sala: GmOnlinePagina | null;
  fonte: Fonte;
  setFonte(f: Fonte): void;
  recentes: readonly JogadorSalvo[];
  fixados: readonly JogadorSalvo[];
  totalOnline: number | null;
  picked: GmOnline | null;
  onEscolher(j: GmOnline): void;
  onEscolherSalvo(j: JogadorSalvo): void;
}

/** A coluna da esquerda: a busca pelo nome no servidor ou uma das listas curtas. Nunca a lista do shard. */
export function ListaDeJogadores(p: ListaProps) {
  const buscando = p.busca.trim().length >= MIN_BUSCA;
  const idioma = idiomaAtual();
  const fixadosIds = new Set(p.fixados.map(f => f.id));
  const linhaOnline = (j: GmOnline) => (
    <button key={j.id} type="button" className="gmx-row" role="option" aria-selected={p.picked?.id === j.id} onClick={() => p.onEscolher(j)}>
      <i className="gmx-dot" style={{ background: j.dead ? 'var(--bad)' : '#6fcf7d' }} title={j.dead ? t('gm.mortoCurto') : t('gm.vivoCurto')} />
      <span style={{ minWidth: 0 }}>
        <b style={{ color: nickFillFromLabel(j.notoriety, { dead: j.dead }) }}>
          {j.name}{j.you ? t('gm.sufixoVoce') : ''}{(j.vipAteMs ?? 0) > Date.now() ? ' ♛' : ''}
        </b>
        <small>{j.guildTag ? `[${j.guildTag}] · ` : ''}{j.city}</small>
      </span>
      <span className="gmx-row__c">{fixadosIds.has(j.id) ? '★ ' : ''}{rotuloCanal(j.canal)}</span>
    </button>
  );
  const linhaSalva = (j: JogadorSalvo) => (
    <button key={j.id} type="button" className="gmx-row" role="option" aria-selected={p.picked?.id === j.id} onClick={() => p.onEscolherSalvo(j)}>
      <i className="gmx-dot" style={{ background: 'var(--ink-dim)' }} />
      <span style={{ minWidth: 0 }}><b>{j.name}</b><small>{t('gm.x.toqueParaVer')}</small></span>
      <span className="gmx-row__c">{fixadosIds.has(j.id) ? '★' : ''}</span>
    </button>
  );

  let corpo;
  if (buscando) {
    corpo = !p.achados ? <p className="gmx-vazio">{t('comum.carregando')}</p>
      : p.achados.itens.length === 0 ? <p className="gmx-vazio">{t('gm.x.ninguemComEsseNome')}</p>
      : p.achados.itens.map(linhaOnline);
  } else if (p.fonte === 'sala') {
    corpo = !p.sala ? <p className="gmx-vazio">{t('comum.carregando')}</p>
      : p.sala.itens.length === 0 ? <p className="gmx-vazio">{t('gm.x.salaVazia')}</p>
      : p.sala.itens.map(linhaOnline);
  } else {
    const lista = p.fonte === 'recentes' ? p.recentes : p.fixados;
    corpo = lista.length === 0
      ? <p className="gmx-vazio">{t(p.fonte === 'recentes' ? 'gm.x.recentesVazio' : 'gm.x.fixadosVazio')}</p>
      : lista.map(linhaSalva);
  }

  const rodape = buscando && p.achados
    ? (p.achados.total > p.achados.itens.length
      ? t('gm.x.achadosDe', { n: p.achados.itens.length, total: p.achados.total.toLocaleString(idioma) })
      : t('gm.x.achados', { n: p.achados.total }))
    : !buscando && p.fonte === 'sala' && p.sala && p.sala.total > p.sala.itens.length
      ? t('gm.x.salaDe', { n: p.sala.itens.length, total: p.sala.total })
      : t('gm.x.naoCarrega', { n: (p.totalOnline ?? 0).toLocaleString(idioma) });

  return (
    <div className="gmx-lista">
      <input type="search" value={p.busca} maxLength={32} placeholder={t('gm.x.buscaNome')} aria-label={t('gm.x.buscaNome')}
        onChange={e => p.setBusca(e.target.value)} />
      <div className="gmx-fontes">
        {([['sala', t('gm.x.fonteSala', { n: p.sala?.total ?? 0 })], ['recentes', t('gm.x.fonteRecentes', { n: p.recentes.length })],
          ['fixados', t('gm.x.fonteFixados', { n: p.fixados.length })]] as const).map(([k, r]) => (
          <button key={k} type="button" className="gmx-chip" aria-pressed={!buscando && p.fonte === k}
            onClick={() => { p.setBusca(''); p.setFonte(k); }}>{r}</button>
        ))}
      </div>
      <div className="gmx-rows" role="listbox" aria-label={t('gm.x.jogadores')}>{corpo}</div>
      <p className="gmx-meta" style={{ marginTop: 5 }}>{rodape}</p>
    </div>
  );
}

interface FichaProps {
  alvo: GmOnline;
  offline: boolean;
  eu: GmOnline | null;
  busy: boolean;
  rodar: Rodar;
  cidades: readonly RegistroMapa[];
  tileX?: number;
  tileY?: number;
  teleportando: boolean;
  fixado: boolean;
  onFixar(): void;
  onIrAte(alvo: GmOnline): void;
  onTeleportar(alvo: GmOnline): void;
  onCancelarTeleporte(): void;
  onMandar(alvo: GmOnline, cidadeId: string): void;
  onEditarSkills(alvo: GmOnline): void;
  onDarItem(alvo: GmOnline): void;
  skillsTick: number;
}

type AbaFicha = 'resumo' | 'banco' | 'guilda';

/** A ficha do jogador escolhido: a barra com as ações de sempre e abas para o resto (techspec-gm-menu §04). */
export function FichaDoJogador(p: FichaProps) {
  const { alvo, busy, rodar } = p;
  const [aba, setAba] = useState<AbaFicha>('resumo');
  const [dest, setDest] = useState('');
  const [prata, setPrata] = useState('');
  const [ouro, setOuro] = useState('');
  const [cofre, setCofre] = useState('');
  const [gNome, setGNome] = useState('');
  const [gTag, setGTag] = useState('');
  const [skills, setSkills] = useState<GmSkills | null>(null);
  const idioma = idiomaAtual();
  const atual = idDaCidade(alvo.city, p.cidades);

  useEffect(() => {
    setDest(p.cidades.find(c => c.id !== atual)?.id ?? p.cidades[0]?.id ?? '');
  }, [alvo.id, atual, p.cidades]);

  useEffect(() => {
    let vivo = true;
    setSkills(null);
    api.gmSkills(alvo.id).then(s => { if (vivo) setSkills(s); }).catch(() => { /* o resumo fica sem as skills */ });
    return () => { vivo = false; };
  }, [alvo.id, p.skillsTick]);

  const mesmaSala = !!(p.eu && !alvo.you && p.eu.city === alvo.city && (p.eu.canal ?? 0) === (alvo.canal ?? 0));
  const podePuxar = mesmaSala && p.tileX != null && p.tileY != null;
  const corNick = nickFillFromLabel(alvo.notoriety, { dead: alvo.dead });
  const vipAte = alvo.vipAteMs ?? 0;
  const off = p.offline || busy;

  const mudarNotoriedade = (n: Notoriety) => {
    const de = alvo.notoriety;
    void rodar(() => api.gmNotoriety(alvo.id, n), {
      cat: t('gm.x.cat.jogador'),
      txt: t('gm.x.reg.notoriedade', { nome: alvo.name, de: t(NOTORIEDADE_CURTA[de]), para: t(NOTORIEDADE_CURTA[n]) }),
      desfazer: de === 'duelista' ? undefined : () => api.gmNotoriety(alvo.id, de),
      alvo
    });
  };

  return (
    <div className="gmx-ficha">
      <div className="gmx-ficha__cab">
        <div className="gmx-avatar" style={{ color: corNick }}>{alvo.name[0]}</div>
        <div style={{ minWidth: 0 }}>
          <h4>
            <span style={{ color: corNick }}>{alvo.name}</span>
            {alvo.you && <span className="gmx-meta">{t('gm.sufixoVoce')}</span>}
            <button type="button" className="btn btn--tiny" onClick={p.onFixar} title={t('gm.x.fixarDica')}>
              {p.fixado ? `★ ${t('gm.x.fixado')}` : `☆ ${t('gm.x.fixar')}`}
            </button>
          </h4>
          <div className="gmx-linha gmx-meta">
            <span className="gmx-tag" style={{ color: corNick }}>{t(NOTORIEDADE_CURTA[alvo.notoriety] ?? 'notoriedade.innocentCurto')}</span>
            <span className="gmx-tag" style={{ color: alvo.dead ? 'var(--bad)' : '#6fcf7d' }}>{alvo.dead ? t('gm.mortoCurto') : t('gm.vivoCurto')}</span>
            {vipAte > 0 && (
              <span className="gm-vip"><CoroaVip />{' '}
                {vipAte > Date.now()
                  ? t('vip.ateCurto', { data: new Date(vipAte).toLocaleDateString(idioma) })
                  : t('vip.venceu', { data: new Date(vipAte).toLocaleDateString(idioma) })}
              </span>
            )}
            <span>{alvo.guildTag ? `[${alvo.guildTag}]` : t('guilda.semGuilda')}</span>
            <span>· {alvo.city} · {t('mundo.canal', { n: alvo.canal ?? 0 })}</span>
            {mesmaSala && <span className="gmx-tag" style={{ color: 'var(--mana)' }}>{t('gm.aqui')}</span>}
            {p.offline && <span className="gmx-tag" style={{ color: 'var(--bad)' }}>{t('gm.x.offline')}</span>}
          </div>
        </div>
      </div>

      <div className="gmx-barra">
        {!alvo.you && (
          <>
            <button type="button" className="btn btn--primary" disabled={off} onClick={() => p.onIrAte(alvo)}>{t('gm.irAte')}</button>
            <button type="button" className="btn" disabled={off || !podePuxar} title={podePuxar ? undefined : t('gm.x.puxarDica')}
              onClick={() => void rodar(() => api.gmTeleport(alvo.id, p.tileX!, p.tileY!), { cat: t('gm.x.cat.mover'), txt: t('gm.x.reg.puxou', { nome: alvo.name }), alvo })}>
              {t('gm.x.puxar')}
            </button>
          </>
        )}
        {p.teleportando
          ? <button type="button" className="btn" onClick={p.onCancelarTeleporte}>{t('gm.cliqueNoMapa')} · {t('comum.cancelar')}</button>
          : <button type="button" className="btn" disabled={off} onClick={() => p.onTeleportar(alvo)}>{t('gm.x.teleportarNoMapa')}</button>}
        <select value={dest} onChange={e => setDest(e.target.value)} disabled={off || p.cidades.length === 0} aria-label={t('gm.cidadeDestino')}>
          {p.cidades.map(c => <option key={c.id} value={c.id}>{c.nome}{c.id === atual ? t('gm.sufixoAtual') : ''}</option>)}
        </select>
        <button type="button" className="btn" disabled={off || !dest || dest === atual} onClick={() => p.onMandar(alvo, dest)}>{t('gm.x.mandar')}</button>
        <span className="gmx-sep" />
        {alvo.dead
          ? <button type="button" className="btn" disabled={off}
              onClick={() => void rodar(() => api.gmRevive(alvo.id), { cat: t('gm.x.cat.jogador'), txt: t('gm.x.reg.reviveu', { nome: alvo.name }), alvo })}>
              {t('gm.reviver')}
            </button>
          : <BotaoPerigoso rotulo={t('gm.matar')} confirmar={t('gm.x.confirmarMatar')} disabled={off}
              onConfirm={() => void rodar(() => api.gmKill(alvo.id), { cat: t('gm.x.cat.jogador'), txt: t('gm.x.reg.matou', { nome: alvo.name }), alvo })} />}
        <span className="gmx-sep" />
        <span className="gmx-seg" role="group" aria-label={t('gm.notoriedade')}>
          {FLAGS.map(f => (
            <button key={f.id} type="button" aria-pressed={alvo.notoriety === f.id} disabled={off || alvo.notoriety === f.id}
              style={{ color: alvo.notoriety === f.id ? undefined : nickFillFromLabel(f.id, {}) }} onClick={() => mudarNotoriedade(f.id)}>
              {t(f.rotulo)}
            </button>
          ))}
        </span>
      </div>

      <div>
        <div className="gmx-abas" role="tablist">
          {(['resumo', 'banco', 'guilda'] as const).map(k => (
            <button key={k} type="button" role="tab" aria-selected={aba === k} onClick={() => setAba(k)}>{t(`gm.x.aba.${k}`)}</button>
          ))}
        </div>
        <div style={{ paddingTop: 10 }}>
          {aba === 'resumo' && (
            <div className="gmx-grade gmx-g2">
              <div className="gmx-card">
                <p className="gmx-h" style={{ marginBottom: 6 }}>{t('gm.secaoSkills')}</p>
                <p className="gmx-meta">
                  {skills
                    ? t('gm.skills.resumo', {
                      n: skills.total.toLocaleString(idioma, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
                      cap: skills.skillCap,
                      k: skills.skills.filter(s => s.value > 0).length
                    })
                    : t('gm.skills.lendo')}
                </p>
                <div className="gmx-linha"><button type="button" className="btn btn--tiny btn--primary" disabled={off || !skills}
                  onClick={() => p.onEditarSkills(alvo)}>{t('gm.skills.editar')}</button></div>
              </div>
              <div className="gmx-card">
                <p className="gmx-h" style={{ marginBottom: 6 }}>{t('gm.x.aba.itens')}</p>
                <p className="gmx-meta">{t('gm.x.darItemDica', { nome: alvo.name })}</p>
                <div className="gmx-linha"><button type="button" className="btn btn--tiny" disabled={off}
                  onClick={() => p.onDarItem(alvo)}>{t('gm.x.abrirItensPara', { nome: alvo.name })}</button></div>
              </div>
            </div>
          )}
          {aba === 'banco' && (
            <div className="gmx-grade gmx-g2">
              <div className="gmx-card">
                <p className="gmx-h" style={{ marginBottom: 6 }}>{t('gm.x.prata')}</p>
                <div className="gmx-linha">
                  <input inputMode="numeric" autoComplete="off" value={prata} placeholder={t('gm.valorPrata')} style={{ width: 120 }}
                    onChange={e => setPrata(e.target.value.replace(/\D/g, ''))} disabled={off} />
                  <button type="button" className="btn" disabled={off || !prata}
                    onClick={() => void rodar(() => api.gmBank(alvo.id, Number(prata), 0), { cat: t('gm.x.cat.banco'), txt: t('gm.x.reg.prata', { nome: alvo.name, n: Number(prata).toLocaleString(idioma) }), alvo }).then(ok => ok && setPrata(''))}>
                    {t('gm.enviarPrata')}
                  </button>
                </div>
                <div className="gmx-linha">
                  {[1000, 10000, 100000].map(v => (
                    <button key={v} type="button" className="btn btn--tiny" disabled={off} onClick={() => setPrata(String(v))}>{v.toLocaleString(idioma)}</button>
                  ))}
                </div>
              </div>
              <div className="gmx-card">
                <p className="gmx-h" style={{ marginBottom: 6 }}>{t('gm.x.ouro')}</p>
                <div className="gmx-linha">
                  <input inputMode="numeric" autoComplete="off" value={ouro} placeholder={t('gm.valorOuro')} style={{ width: 120 }}
                    onChange={e => setOuro(e.target.value.replace(/\D/g, ''))} disabled={off} />
                  <button type="button" className="btn" disabled={off || !ouro}
                    onClick={() => void rodar(() => api.gmBank(alvo.id, 0, Number(ouro)), { cat: t('gm.x.cat.banco'), txt: t('gm.x.reg.ouro', { nome: alvo.name, n: ouro }), alvo }).then(ok => ok && setOuro(''))}>
                    {t('gm.enviarOuro')}
                  </button>
                </div>
                <div className="gmx-linha">
                  {[1, 10, 100].map(v => (
                    <button key={v} type="button" className="btn btn--tiny" disabled={off} onClick={() => setOuro(String(v))}>{v}</button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {aba === 'guilda' && (alvo.guildTag ? (
            <div className="gmx-card">
              <div className="gmx-linha">
                <span>{t('gm.x.membroDe', { tag: alvo.guildTag, nome: alvo.guildName || t('guilda.titulo') })}</span>
                <span className="gmx-esp" />
                <BotaoPerigoso rotulo={t('gm.x.tirarDaGuilda')} confirmar={t('gm.x.confirmarTirar')} disabled={off}
                  onConfirm={() => void rodar(() => api.gmLeaveGuild(alvo.id), { cat: t('gm.x.cat.guilda'), txt: t('gm.x.reg.tirouGuilda', { nome: alvo.name, tag: alvo.guildTag ?? '' }), alvo })} />
              </div>
              <div className="gmx-linha">
                <input inputMode="numeric" autoComplete="off" value={cofre} placeholder={t('gm.ouroNoCofre')} style={{ width: 140 }}
                  onChange={e => setCofre(e.target.value.replace(/\D/g, ''))} disabled={off} />
                <button type="button" className="btn" disabled={off || !cofre}
                  onClick={() => void rodar(() => api.gmGuildVault(alvo.id, Number(cofre)), { cat: t('gm.x.cat.guilda'), txt: t('gm.x.reg.cofre', { tag: alvo.guildTag ?? '', n: cofre }), alvo }).then(ok => ok && setCofre(''))}>
                  {t('gm.ouroNoCofre')}
                </button>
              </div>
            </div>
          ) : (
            <div className="gmx-card">
              <p className="gmx-meta">{t('guilda.semGuilda')}</p>
              <div className="gmx-linha">
                <input value={gNome} maxLength={20} placeholder={t('gm.x.nomeDaGuilda')} onChange={e => setGNome(e.target.value)} disabled={off} />
                <input value={gTag} maxLength={4} placeholder="TAG" style={{ width: 70 }} onChange={e => setGTag(e.target.value.toUpperCase())} disabled={off} />
                <button type="button" className="btn" disabled={off || gNome.trim().length < 2 || !gTag.trim()}
                  onClick={() => void rodar(() => api.gmCreateGuild(alvo.id, gNome.trim(), gTag.trim()), { cat: t('gm.x.cat.guilda'), txt: t('gm.x.reg.criouGuilda', { tag: gTag.trim(), nome: alvo.name }), alvo })}>
                  {t('gm.x.criarGuilda', { nome: alvo.name })}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
